import { Menu } from "@/types/menu";

interface LanguageInfo {
  code: string;
  label: string;
  nativeName: string;
  region: 'east-asia' | 'southeast-asia' | 'south-asia' | 'europe' | 'middle-east' | 'other';
}

export const SUPPORTED_LANGUAGES: LanguageInfo[] = [
  // East Asian Languages
  { 
    code: "chinese-simplified",
    label: "Mandarin (Simplified)",
    nativeName: "简体中文",
    region: "east-asia"
  },
  { 
    code: "chinese-traditional",
    label: "Mandarin (Traditional)",
    nativeName: "繁體中文",
    region: "east-asia"
  },
  { 
    code: "cantonese",
    label: "Cantonese",
    nativeName: "廣東話",
    region: "east-asia"
  },
  { 
    code: "korean",
    label: "Korean",
    nativeName: "한국어",
    region: "east-asia"
  },
  { 
    code: "japanese",
    label: "Japanese",
    nativeName: "日本語",
    region: "east-asia"
  },

  // Southeast Asian Languages
  { 
    code: "thai",
    label: "Thai",
    nativeName: "ไทย",
    region: "southeast-asia"
  },
  { 
    code: "vietnamese",
    label: "Vietnamese",
    nativeName: "Tiếng Việt",
    region: "southeast-asia"
  },
  { 
    code: "indonesian",
    label: "Indonesian",
    nativeName: "Bahasa Indonesia",
    region: "southeast-asia"
  },
  { 
    code: "malay",
    label: "Malay",
    nativeName: "Bahasa Melayu",
    region: "southeast-asia"
  },
  { 
    code: "filipino",
    label: "Filipino",
    nativeName: "Filipino",
    region: "southeast-asia"
  },

  // South Asian Languages
  { 
    code: "hindi",
    label: "Hindi",
    nativeName: "हिन्दी",
    region: "south-asia"
  },
  { 
    code: "bhojpuri",
    label: "Bhojpuri",
    nativeName: "भोजपुरी",
    region: "south-asia"
  },

  // Middle Eastern Languages
  {
    code: "arabic",
    label: "Arabic",
    nativeName: "العربية",
    region: "middle-east"
  },
  {
    code: "persian",
    label: "Persian",
    nativeName: "فارسی",
    region: "middle-east"
  },
  {
    code: "hebrew",
    label: "Hebrew",
    nativeName: "עברית",
    region: "middle-east"
  },
  {
    code: "turkish",
    label: "Turkish",
    nativeName: "Türkçe",
    region: "middle-east"
  },

  // European Languages
  { 
    code: "english",
    label: "English",
    nativeName: "English",
    region: "europe"
  },
  { 
    code: "french",
    label: "French",
    nativeName: "Français",
    region: "europe"
  },
  { 
    code: "german",
    label: "German",
    nativeName: "Deutsch",
    region: "europe"
  },
  { 
    code: "italian",
    label: "Italian",
    nativeName: "Italiano",
    region: "europe"
  },
  { 
    code: "russian",
    label: "Russian",
    nativeName: "Русский",
    region: "europe"
  },
  { 
    code: "spanish",
    label: "Spanish",
    nativeName: "Español",
    region: "europe"
  },
  {
    code: "czech",
    label: "Czech",
    nativeName: "Čeština",
    region: "europe"
  },
  {
    code: "dutch",
    label: "Dutch",
    nativeName: "Nederlands",
    region: "europe"
  },
  {
    code: "polish",
    label: "Polish",
    nativeName: "Polski",
    region: "europe"
  },
  {
    code: "portuguese",
    label: "Portuguese",
    nativeName: "Português",
    region: "europe"
  },
  {
    code: "portuguese-brazilian",
    label: "Portuguese (Brazilian)",
    nativeName: "Português Brasileiro",
    region: "europe"
  },
  {
    code: "romanian",
    label: "Romanian",
    nativeName: "Română",
    region: "europe"
  },
  {
    code: "slovenian",
    label: "Slovenian",
    nativeName: "Slovenščina",
    region: "europe"
  },
  {
    code: "swedish",
    label: "Swedish",
    nativeName: "Svenska",
    region: "europe"
  }
];

export function getLanguageInfo(code: string): LanguageInfo | undefined {
  return SUPPORTED_LANGUAGES.find(lang => lang.code === code);
}

export function getLanguageLabel(code: string, useNative: boolean = true): string {
  const language = getLanguageInfo(code);
  if (!language) return code;
  return useNative ? `${language.nativeName} (${language.label})` : language.label;
}

export function getLanguagesByRegion(region: string): LanguageInfo[] {
  return SUPPORTED_LANGUAGES.filter(lang => lang.region === region);
}

// Token processing constants based on real-world measurements
const BASE_TIME = 20; // Base time in seconds for API setup and initial processing
const CATEGORY_TIME_FACTOR = 2.5; // Additional time per category
const ITEM_TIME_FACTOR = 0.5; // Additional time per item
const NETWORK_DELAY = 3; // Network delay in seconds
const SEQUENTIAL_DELAY = 2; // Small delay between sequential translations
const DESCRIPTION_TIME_FACTOR = 0.3; // Additional time for items with descriptions

export interface TimeEstimate {
  totalTime: number;          // Total time for all languages
  timePerLanguage: number;    // Time per individual language
  startOffsets: {            // When each language translation will start
    [key: string]: number;
  };
}

export function calculateTranslationEstimate(menu: Menu, languages: string[]): TimeEstimate {
  // Calculate total number of items and items with descriptions
  const itemsWithDescriptions = menu.categories.reduce(
    (sum, category) => sum + category.items.filter(item => item.description).length,
    0
  );
  
  const totalItems = menu.categories.reduce(
    (sum, category) => sum + category.items.length,
    0
  );

  // Calculate time per language based on menu complexity
  const timePerLanguage = Math.round(
    BASE_TIME + 
    (menu.categories.length * CATEGORY_TIME_FACTOR) +
    (totalItems * ITEM_TIME_FACTOR) +
    (itemsWithDescriptions * DESCRIPTION_TIME_FACTOR) +
    NETWORK_DELAY
  );
  
  // Calculate start time offset for each language
  const startOffsets: { [key: string]: number } = {};
  languages.forEach((lang, index) => {
    startOffsets[lang] = index * (timePerLanguage + SEQUENTIAL_DELAY);
  });
  
  // Total time is the sum of all individual times plus sequential delays
  const totalTime = (languages.length * timePerLanguage) + 
    ((languages.length - 1) * SEQUENTIAL_DELAY);
  
  return {
    totalTime,
    timePerLanguage,
    startOffsets
  };
} 